import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import HisobAppPreview from '@/components/marketing/HisobAppPreview';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import {
  useCampaignItems, useEligibleCategories, useMarketingAudit, useMarketingCampaign,
  useMarketingMerchants, useMarketingMutation,
} from '@/hooks/use-marketing';
import {
  CAMPAIGN_TRANSITIONS, bpsToPercent, campaignStatusLabel, campaignStatusVariant,
  formatPeriod, promotionLabel, type CampaignStatus, type PromotionType,
} from '@/lib/marketing';

export default function AdminMarketingCampaignDetail() {
  const { t, i18n } = useTranslation();
  const locale = ({ uz: 'uz-UZ', ru: 'ru-RU', en: 'en-US' } as Record<string, string>)[(i18n.language || 'uz').slice(0, 2)] ?? 'uz-UZ';
  const { id } = useParams();
  const { data: campaign } = useMarketingCampaign(id);
  // FR-97: auditor reads only; owner/support/finance run the campaign (backend authorizes the same).
  const { hasRole } = useAdminAuth();
  const canEdit = hasRole('platform_owner', 'support_admin', 'finance_admin');
  const { data: items = [] } = useCampaignItems(id);
  const { data: merchants = [] } = useMarketingMerchants();
  const { data: audit = [] } = useMarketingAudit(undefined, undefined, { adminView: true, campaignId: id });

  const setStatus = useMarketingMutation('set_campaign_status');
  const addItem = useMarketingMutation('add_campaign_item');
  const updateItem = useMarketingMutation('update_campaign_item');
  const removeItem = useMarketingMutation('remove_campaign_item');
  const reorder = useMarketingMutation('reorder_campaign_items');
  const duplicate = useMarketingMutation('duplicate_campaign');

  const [addOpen, setAddOpen] = useState(false);
  const [draftRef, setDraftRef] = useState(() => crypto.randomUUID());
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState({ merchant_id: '', category_id: '', promo_title: '', promo_subtitle: '', promo_image_url: '' });
  const { data: categories = [] } = useEligibleCategories(newItem.merchant_id || undefined, { adminView: true });

  if (!campaign) {
    return <div className="p-6 text-muted-foreground">{t('mkt_campaign_detail.loading')}</div>;
  }

  const promotionType = campaign.promotion_type as PromotionType;
  const status = campaign.status as CampaignStatus;
  const nextStatuses = CAMPAIGN_TRANSITIONS[status];

  const handleAdd = () => {
    if (!newItem.merchant_id) return toast.error(t('mkt_campaign_detail.selectStore'));
    if (promotionType === 'PROMOTED_CATEGORIES' && !newItem.category_id) return toast.error(t('mkt_campaign_detail.selectCategory'));
    addItem.mutate(
      {
        campaign_id: id,
        merchant_id: newItem.merchant_id,
        category_id: promotionType === 'PROMOTED_CATEGORIES' ? newItem.category_id : null,
        promo_title: newItem.promo_title,
        promo_subtitle: newItem.promo_subtitle,
        promo_image_url: newItem.promo_image_url,
      },
      {
        onSuccess: () => {
          toast.success(t('mkt_campaign_detail.itemAdded'));
          setAddOpen(false);
          setNewItem({ merchant_id: '', category_id: '', promo_title: '', promo_subtitle: '', promo_image_url: '' });
        },
        onError: (e: any) => toast.error(e.message),
      },
    );
  };

  const applyOrder = (list: any[]) => {
    reorder.mutate(
      { campaign_id: id, order: list.map((it, i) => ({ id: it.id, display_order: i + 1 })) },
      { onError: (e: any) => toast.error(e.message) },
    );
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    applyOrder(next);
  };

  const handleDrop = (to: number) => {
    if (dragIndex === null || dragIndex === to) return setDragIndex(null);
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(to, 0, moved);
    setDragIndex(null);
    applyOrder(next);
  };

  return (
    <div className="mx-auto max-w-6xl animate-fade-in p-6">
      <Link to="/admin/marketing/campaigns" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> {t('mkt_campaign_detail.backToCampaigns')}
      </Link>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground">{campaign.name}</h1>
          <p className="text-sm text-muted-foreground">
            {promotionLabel[promotionType]} · {formatPeriod(campaign.start_at, campaign.end_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={campaignStatusVariant[status]}>{campaignStatusLabel[status]}</Badge>
          {canEdit && nextStatuses.map((s) => (
            <Button key={s} size="sm" variant={s === 'CANCELLED' ? 'destructive' : 'outline'}
              onClick={() => setStatus.mutate({ id, status: s }, {
                onSuccess: () => toast.success(t('mkt_campaign_detail.statusToast', { status: campaignStatusLabel[s] })),
                onError: (e: any) => toast.error(e.message),
              })}>
              {campaignStatusLabel[s]}
            </Button>
          ))}
          {canEdit && (
            <Button size="sm" variant="ghost" onClick={() => duplicate.mutate({ id }, {
              onSuccess: () => toast.success(t('mkt_campaign_detail.duplicated')),
              onError: (e: any) => toast.error(e.message),
            })}>
              <Copy size={14} className="mr-1" /> {t('mkt_campaign_detail.duplicate')}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">{t('mkt_campaign_detail.tabItems')} ({items.length})</TabsTrigger>
          <TabsTrigger value="preview">{t('mkt_campaign_detail.tabPreview')}</TabsTrigger>
          <TabsTrigger value="history">{t('mkt_campaign_detail.tabHistory')}</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">{t('mkt_campaign_detail.dragHint')}</p>
            {canEdit && <Button size="sm" onClick={() => { setDraftRef(crypto.randomUUID()); setAddOpen(true); }}><Plus size={14} className="mr-1" /> {t('mkt_campaign_detail.addItem')}</Button>}
          </div>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">#</th>
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaign_detail.colStore')}</th>
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaign_detail.colDetails')}</th>
                  <th className="p-3 text-left text-xs font-medium text-muted-foreground">{t('mkt_campaign_detail.colActive')}</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t('mkt_campaign_detail.noItems')}</td></tr>
                ) : items.map((it: any, i: number) => (
                  <tr
                    key={it.id}
                    draggable={canEdit}
                    onDragStart={() => setDragIndex(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(i)}
                    onDragEnd={() => setDragIndex(null)}
                    className={`cursor-grab border-b border-border last:border-0 ${dragIndex === i ? 'opacity-50' : ''}`}
                  >
                    <td className="p-3 text-sm text-muted-foreground">{it.display_order}</td>
                    <td className="p-3">
                      <p className="text-sm font-medium text-foreground">{it.merchant?.name ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">{it.promo_title || '—'}</p>
                      {it.cancellation_requested_at && (
                        <div className="mt-1">
                          <Badge variant="destructive">{t('mkt_campaign_detail.cancellationRequested')}</Badge>
                          {it.cancellation_reason && <p className="mt-1 text-xs text-muted-foreground">{t('mkt_campaign_detail.reason', { reason: it.cancellation_reason })}</p>}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {it.category?.name ? t('mkt_campaign_detail.categoryLine', { name: it.category.name }) : null}
                      {it.cashback_offer?.cashback_bps ? t('mkt_campaign_detail.cashbackLine', { value: bpsToPercent(it.cashback_offer.cashback_bps) }) : null}
                      {!it.category?.name && !it.cashback_offer?.cashback_bps ? '—' : null}
                    </td>
                    <td className="p-3">
                      <Switch checked={it.is_active} disabled={!canEdit} onCheckedChange={(v) => updateItem.mutate({ id: it.id, updates: { is_active: v } })} />
                    </td>
                    <td className="p-3">
                      {canEdit && <div className="flex items-center justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => move(i, -1)}><ArrowUp size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => move(i, 1)}><ArrowDown size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => removeItem.mutate({ id: it.id }, {
                          onSuccess: () => toast.success(t('mkt_campaign_detail.deleted')),
                          onError: (e: any) => toast.error(e.message),
                        })}>
                          <Trash2 size={14} className="text-destructive" />
                        </Button>
                      </div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <HisobAppPreview
            promotionType={promotionType}
            items={items.filter((it: any) => it.is_active).map((it: any) => ({
              id: it.id,
              merchant_name: it.merchant?.name,
              category_name: it.category?.name,
              promo_title: it.promo_title,
              promo_subtitle: it.promo_subtitle,
              promo_image_url: it.promo_image_url,
              cashback_bps: it.cashback_offer?.cashback_bps,
            }))}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {audit.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_campaign_detail.historyEmpty')}</p>
            ) : audit.map((a: any) => (
              <div key={a.id} className="p-3">
                <p className="text-sm text-foreground">{a.action}</p>
                {a.action === 'cancellation_requested' && a.new_value?.reason && (
                  <p className="text-xs text-muted-foreground">{t('mkt_campaign_detail.reason', { reason: a.new_value.reason })}</p>
                )}
                <p className="text-xs text-muted-foreground">{a.actor_role} · {new Date(a.created_at).toLocaleString(locale)}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('mkt_campaign_detail.addItem')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('mkt_campaign_detail.colStore')}</Label>
              <Select value={newItem.merchant_id || '__none__'} onValueChange={(v) => setNewItem({ ...newItem, merchant_id: v === '__none__' ? '' : v, category_id: '' })}>
                <SelectTrigger><SelectValue placeholder={t('mkt_campaign_detail.selectStore')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('mkt_campaign_detail.notSelected')}</SelectItem>
                  {merchants.map((m: any) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {promotionType === 'PROMOTED_CATEGORIES' && (
              <div>
                <Label>{t('mkt_campaign_detail.categoryFromProducts')}</Label>
                <Select value={newItem.category_id || '__none__'} onValueChange={(v) => setNewItem({ ...newItem, category_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder={t('mkt_campaign_detail.selectCategory')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('mkt_campaign_detail.notSelected')}</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t('mkt_campaign_detail.promoTitle')}</Label>
              <Input value={newItem.promo_title} onChange={(e) => setNewItem({ ...newItem, promo_title: e.target.value })} />
            </div>
            <div>
              <Label>{t('mkt_campaign_detail.promoSubtitle')}</Label>
              <Input value={newItem.promo_subtitle} onChange={(e) => setNewItem({ ...newItem, promo_subtitle: e.target.value })} />
            </div>
            <MediaUploadField
              kind="marketing_image"
              targetId={newItem.merchant_id || null}
              refId={draftRef}
              value={newItem.promo_image_url}
              onChange={(url) => setNewItem({ ...newItem, promo_image_url: url })}
              label={t('mkt_campaign_detail.promoImage')}
              disabled={!newItem.merchant_id}
              disabledReason={t('mkt_campaign_detail.selectStoreFirst')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>{t('mkt_campaign_detail.cancel')}</Button>
            <Button onClick={handleAdd} disabled={addItem.isPending}>{t('mkt_campaign_detail.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
